import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../../services/api';
import toast from 'react-hot-toast';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  ArrowPathIcon,
  TableCellsIcon,
  DocumentIcon,
  ChevronRightIcon,
  MagnifyingGlassIcon,
  ArrowRightStartOnRectangleIcon
} from '@heroicons/react/24/outline';

// Google Sheets icon component
const GoogleSheetsIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M19.385 2H4.615C3.17 2 2 3.17 2 4.615v14.77C2 20.83 3.17 22 4.615 22h14.77c1.445 0 2.615-1.17 2.615-2.615V4.615C22 3.17 20.83 2 19.385 2zM8 18H6v-2h2v2zm0-4H6v-2h2v2zm0-4H6V8h2v2zm10 8h-8v-2h8v2zm0-4h-8v-2h8v2zm0-4h-8V8h8v2z"/>
  </svg>
);

// ============ Google Picker API Loader ============

let pickerApiLoaded = false;
let pickerApiLoading = false;
let pickerApiLoadCallbacks = [];

/**
 * Dynamically load the Google Picker API.
 * Loads gapi.js and then the 'picker' module.
 */
function loadGooglePickerApi() {
  return new Promise((resolve, reject) => {
    // Already loaded
    if (pickerApiLoaded && window.google?.picker) {
      resolve();
      return;
    }

    // Loading in progress - queue the callback
    if (pickerApiLoading) {
      pickerApiLoadCallbacks.push({ resolve, reject });
      return;
    }

    pickerApiLoading = true;
    pickerApiLoadCallbacks.push({ resolve, reject });

    const flushCallbacks = (error) => {
      const cbs = [...pickerApiLoadCallbacks];
      pickerApiLoadCallbacks = [];
      pickerApiLoading = false;
      cbs.forEach(cb => error ? cb.reject(error) : cb.resolve());
    };

    const loadPickerModule = () => {
      window.gapi.load('picker', {
        callback: () => {
          pickerApiLoaded = true;
          flushCallbacks(null);
        },
        onerror: () => {
          flushCallbacks(new Error('Failed to load Google Picker module'));
        }
      });
    };

    // If gapi is already loaded (e.g. from another script)
    if (window.gapi) {
      loadPickerModule();
      return;
    }

    // Dynamically inject gapi script
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    script.async = true;
    script.defer = true;
    script.onload = loadPickerModule;
    script.onerror = () => {
      flushCallbacks(new Error('Failed to load Google API script'));
    };
    document.head.appendChild(script);
  });
}

/**
 * Open Google Picker filtered to Spreadsheets.
 * Returns a Promise that resolves with the selected document or null if cancelled.
 */
function openGooglePicker(accessToken) {
  return new Promise(async (resolve, reject) => {
    try {
      await loadGooglePickerApi();
    } catch (err) {
      reject(err);
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    // The App ID is the GCP project number (first segment of the Client ID)
    const appId = clientId?.split('-')[0];

    if (!apiKey || apiKey === 'YOUR_GOOGLE_API_KEY_HERE') {
      reject(new Error('Google API Key is not configured. Please set REACT_APP_GOOGLE_API_KEY in your .env file.'));
      return;
    }

    // View: show only spreadsheets from My Drive
    const myDriveView = new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS)
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false)
      .setMode(window.google.picker.DocsViewMode.LIST);

    // View: show spreadsheets from shared drives
    const sharedDriveView = new window.google.picker.DocsView(window.google.picker.ViewId.SPREADSHEETS)
      .setEnableDrives(true)
      .setIncludeFolders(false)
      .setSelectFolderEnabled(false)
      .setMode(window.google.picker.DocsViewMode.LIST);

    const pickerBuilder = new window.google.picker.PickerBuilder()
      .setOAuthToken(accessToken)
      .setDeveloperKey(apiKey)
      .addView(myDriveView)
      .addView(sharedDriveView)
      .setTitle('Select a Google Spreadsheet')
      .setCallback((data) => {
        if (data.action === window.google.picker.Action.PICKED) {
          const doc = data.docs[0];
          resolve({
            id: doc.id,
            name: doc.name,
            url: doc.url,
            lastEditedUtc: doc.lastEditedUtc,
            mimeType: doc.mimeType
          });
        } else if (data.action === window.google.picker.Action.CANCEL) {
          resolve(null);
        }
      });

    // App ID helps scope access to the correct project
    if (appId) {
      pickerBuilder.setAppId(appId);
    }

    const picker = pickerBuilder.build();
    picker.setVisible(true);
  });
}

// ============ Main Component ============

export default function GoogleSheetsSource({ companyId, onSuccess, onBack }) {
  const [searchParams] = useSearchParams();
  
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [tokens, setTokens] = useState(null);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [googleAccount, setGoogleAccount] = useState(null); // { email, name, picture }
  const [isCheckingAuth, setIsCheckingAuth] = useState(true); // Check saved auth on mount
  
  // Data state
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [columns, setColumns] = useState([]);
  const [selectedColumns, setSelectedColumns] = useState(new Set());
  const [columnSearchTerm, setColumnSearchTerm] = useState('');
  const [previewData, setPreviewData] = useState([]);
  
  // Form state
  const [name, setName] = useState('');
  const [headerRow, setHeaderRow] = useState(1);
  
  // UI state
  const [step, setStep] = useState(1); // 1: auth, 2: select spreadsheet, 3: select sheet, 4: configure
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isPickerLoading, setIsPickerLoading] = useState(false);

  // Check for existing Google auth on mount (before checking URL params)
  useEffect(() => {
    checkExistingGoogleAuth();
  }, []);

  // Check for OAuth callback URL params
  useEffect(() => {
    const googleAuth = searchParams.get('googleAuth');
    const tokenData = searchParams.get('tokenData');
    const error = searchParams.get('error');
    
    if (error) {
      toast.error(`Google authentication failed: ${error}`);
      setIsCheckingAuth(false);
      return;
    }
    
    if (googleAuth === 'success' && tokenData) {
      try {
        const decoded = JSON.parse(atob(tokenData));
        setTokens(decoded);
        setIsAuthenticated(true);
        setStep(2);
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
        toast.success('Google account connected successfully!');
        setIsCheckingAuth(false);
        // Refresh auth status to get saved account info
        checkExistingGoogleAuth(true);
      } catch (e) {
        console.error('Failed to decode token data:', e);
        toast.error('Failed to process authentication');
        setIsCheckingAuth(false);
      }
    }
  }, [searchParams]);

  /**
   * Check if the user already has a saved Google integration.
   * If skipStepChange is true, only update account info without changing step.
   */
  const checkExistingGoogleAuth = async (skipStepChange = false) => {
    try {
      const result = await api.get('/data-sources/google/auth-status');
      
      if (result.data.connected) {
        setTokens(result.data.tokens);
        setGoogleAccount(result.data.account);
        setIsAuthenticated(true);
        if (!skipStepChange) {
          setStep(2); // Skip to Picker
        }
      }
    } catch (error) {
      // If 401/403, user isn't authenticated to the app - that's fine
      console.warn('Could not check Google auth status:', error.message);
    } finally {
      setIsCheckingAuth(false);
    }
  };

  // Preload the Picker API after authentication
  useEffect(() => {
    if (isAuthenticated) {
      loadGooglePickerApi().catch(() => {
        // Silently fail - will retry when user clicks the button
      });
    }
  }, [isAuthenticated]);
  
  const handleGoogleAuth = async () => {
    setIsAuthenticating(true);
    
    try {
      const result = await api.post('/data-sources/google/auth', {
        companyId,
        returnUrl: window.location.href
      });
      
      // Redirect to Google OAuth
      window.location.href = result.data.authUrl;
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.error || 'Failed to start Google authentication');
      setIsAuthenticating(false);
    }
  };

  const handleSwitchAccount = async () => {
    // Disconnect the current Google account
    try {
      await api.post('/data-sources/google/disconnect');
    } catch (err) {
      console.warn('Failed to disconnect Google account:', err.message);
    }

    // Clear local state
    setTokens(null);
    setGoogleAccount(null);
    setIsAuthenticated(false);
    setSelectedSpreadsheet(null);
    setSheets([]);
    setSelectedSheet(null);
    setColumns([]);
    setSelectedColumns(new Set());
    setPreviewData([]);
    setName('');
    setStep(1);

    // Initiate new OAuth flow with prompt to select account
    setIsAuthenticating(true);
    try {
      const result = await api.post('/data-sources/google/auth', {
        companyId,
        returnUrl: window.location.href
      });

      // Add login_hint removal and prompt=consent&select_account to force account picker
      const authUrl = new URL(result.data.authUrl);
      authUrl.searchParams.set('prompt', 'consent select_account');
      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Google auth error:', error);
      toast.error(error.response?.data?.error || 'Failed to start Google authentication');
      setIsAuthenticating(false);
    }
  };

  const handleOpenPicker = useCallback(async () => {
    if (!tokens?.accessToken) {
      toast.error('Not authenticated. Please sign in with Google first.');
      return;
    }

    setIsPickerLoading(true);

    try {
      const selected = await openGooglePicker(tokens.accessToken);

      if (selected) {
        setSelectedSpreadsheet(selected);

        // Automatically load spreadsheet metadata (sheets/tabs)
        setIsLoading(true);
        try {
          const result = await api.post('/data-sources/google/spreadsheet-info', {
            accessToken: tokens.accessToken,
            refreshToken: tokens.refreshToken,
            spreadsheetId: selected.id
          });

          setSheets(result.data.spreadsheet.sheets || []);
          setName(selected.name); // Default name to spreadsheet name
          setStep(3);
        } catch (err) {
          console.error('Get spreadsheet info error:', err);
          toast.error('Failed to load spreadsheet details. The file may not be accessible.');
        } finally {
          setIsLoading(false);
        }
      }
    } catch (err) {
      console.error('Google Picker error:', err);
      toast.error(err.message || 'Failed to open file picker');
    } finally {
      setIsPickerLoading(false);
    }
  }, [tokens]);
  
  const handleSelectSheet = async (sheet) => {
    setSelectedSheet(sheet);
    setIsLoading(true);
    
    try {
      const result = await api.post('/data-sources/google/preview-sheet', {
        accessToken: tokens.accessToken,
        refreshToken: tokens.refreshToken,
        spreadsheetId: selectedSpreadsheet.id,
        sheetName: sheet.title,
        headerRow
      });
      
      setColumns(result.data.columns || []);
      setSelectedColumns(new Set(result.data.columns?.map(c => c.name) || []));
      setPreviewData(result.data.preview || []);
      setStep(4);
    } catch (error) {
      console.error('Preview sheet error:', error);
      toast.error('Failed to preview sheet');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleColumnToggle = (columnName) => {
    const updated = new Set(selectedColumns);
    if (updated.has(columnName)) {
      updated.delete(columnName);
    } else {
      updated.add(columnName);
    }
    setSelectedColumns(updated);
  };
  
  const handleSelectAllColumns = () => {
    if (selectedColumns.size === columns.length) {
      setSelectedColumns(new Set());
    } else {
      setSelectedColumns(new Set(columns.map(c => c.name)));
    }
  };
  
  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a name');
      return;
    }
    
    if (selectedColumns.size === 0) {
      toast.error('Please select at least one column');
      return;
    }
    
    setIsCreating(true);
    
    try {
      // Get full column schema for selected columns
      const selectedColumnSchema = columns.filter(c => selectedColumns.has(c.name));
      
      const connectionConfig = {
        spreadsheetId: selectedSpreadsheet.id,
        spreadsheetName: selectedSpreadsheet.name,
        sheetName: selectedSheet.title,
        sheetId: selectedSheet.sheetId,
        headerRow,
        columns: Array.from(selectedColumns),
        googleAccessToken: tokens.accessToken,
        googleRefreshToken: tokens.refreshToken,
        googleTokenExpiry: tokens.expiry
      };
      
      const result = await api.post('/data-sources', {
        name,
        type: 'googlesheets',
        companyId,
        connectionConfig,
        schemaInfo: {
          tables: [{
            name: selectedSheet.title,
            columns: selectedColumnSchema
          }]
        }
      });
      
      onSuccess(result.data.dataSource);
    } catch (error) {
      console.error('Create error:', error);
      toast.error(error.response?.data?.error || 'Failed to create data source');
    } finally {
      setIsCreating(false);
    }
  };

  // Show loading state while checking for existing auth
  if (isCheckingAuth) {
    return (
      <div className="flex items-center justify-center py-16">
        <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center gap-4">
        {[
          { num: 1, label: 'Connect' },
          { num: 2, label: 'Spreadsheet' },
          { num: 3, label: 'Sheet' },
          { num: 4, label: 'Configure' }
        ].map((s, i) => (
          <div key={s.num} className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
              step >= s.num 
                ? 'bg-emerald-600 text-white' 
                : 'bg-surface-200 dark:bg-surface-700 text-surface-500'
            }`}>
              {step > s.num ? <CheckCircleIcon className="w-5 h-5" /> : s.num}
            </div>
            <span className={`text-sm ${step >= s.num ? 'text-surface-900 dark:text-white' : 'text-surface-500'}`}>
              {s.label}
            </span>
            {i < 3 && <div className="w-12 h-px bg-surface-200 dark:bg-surface-700" />}
          </div>
        ))}
      </div>
      
      {/* Step 1: Google Authentication */}
      {step === 1 && (
        <div className="card p-6 space-y-6">
          <div className="flex items-center gap-3 mb-4">
            <GoogleSheetsIcon className="w-8 h-8 text-emerald-500" />
            <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
              Connect to Google Sheets
            </h3>
          </div>
          
          <div className="text-center py-8">
            <GoogleSheetsIcon className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
              Sign in with Google
            </h4>
            <p className="text-surface-600 dark:text-surface-400 mb-6 max-w-md mx-auto">
              Connect your Google account to import data from your spreadsheets. You'll choose exactly which spreadsheets to share using Google's secure file picker.
            </p>
            
            <button
              onClick={handleGoogleAuth}
              disabled={isAuthenticating}
              className="inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-surface-800 border border-surface-300 dark:border-surface-600 rounded-lg shadow-sm hover:bg-surface-50 dark:hover:bg-surface-700 transition-colors"
            >
              {isAuthenticating ? (
                <ArrowPathIcon className="w-5 h-5 animate-spin text-surface-600" />
              ) : (
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              <span className="font-medium text-surface-900 dark:text-white">
                {isAuthenticating ? 'Connecting...' : 'Sign in with Google'}
              </span>
            </button>
          </div>
          
          <div className="flex justify-between pt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 2: Select Spreadsheet via Google Picker */}
      {step === 2 && (
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <GoogleSheetsIcon className="w-8 h-8 text-emerald-500" />
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Select Spreadsheet
              </h3>
            </div>
          </div>

          {/* Connected Google Account Banner */}
          <div className="flex items-center justify-between p-3 mb-6 rounded-lg bg-surface-50 dark:bg-surface-800 border border-surface-200 dark:border-surface-700">
            <div className="flex items-center gap-3">
              {googleAccount?.picture ? (
                <img 
                  src={googleAccount.picture} 
                  alt="" 
                  className="w-8 h-8 rounded-full"
                  referrerPolicy="no-referrer"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                  <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" viewBox="0 0 24 24" fill="currentColor">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
              )}
              <div>
                {googleAccount?.email ? (
                  <>
                    <p className="text-sm font-medium text-surface-900 dark:text-white">
                      {googleAccount.name || googleAccount.email}
                    </p>
                    {googleAccount.name && (
                      <p className="text-xs text-surface-500">{googleAccount.email}</p>
                    )}
                  </>
                ) : (
                  <p className="text-sm font-medium text-surface-900 dark:text-white">
                    Google Account Connected
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleSwitchAccount}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-surface-600 dark:text-surface-400 hover:text-surface-900 dark:hover:text-white bg-white dark:bg-surface-700 border border-surface-200 dark:border-surface-600 rounded-md hover:bg-surface-50 dark:hover:bg-surface-600 transition-colors"
            >
              <ArrowRightStartOnRectangleIcon className="w-3.5 h-3.5" />
              Switch Account
            </button>
          </div>

          {/* Selected spreadsheet info (shown after picker selection while loading sheets) */}
          {selectedSpreadsheet && isLoading ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
              <p className="text-surface-600 dark:text-surface-400">
                Loading sheets from <span className="font-medium text-surface-900 dark:text-white">{selectedSpreadsheet.name}</span>...
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center py-8 gap-6">
              {/* Picker illustration / prompt */}
              <div className="w-20 h-20 rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 flex items-center justify-center">
                <GoogleSheetsIcon className="w-10 h-10 text-emerald-500" />
              </div>

              <div className="text-center max-w-md">
                <h4 className="text-lg font-medium text-surface-900 dark:text-white mb-2">
                  Choose a Spreadsheet from Google Drive
                </h4>
                <p className="text-surface-600 dark:text-surface-400 text-sm">
                  Click the button below to open Google's file picker. You can browse your Drive and Shared Drives to find the spreadsheet you want to import.
                </p>
              </div>

              <button
                onClick={handleOpenPicker}
                disabled={isPickerLoading}
                className="inline-flex items-center gap-3 px-6 py-3 bg-white dark:bg-surface-800 border-2 border-emerald-300 dark:border-emerald-700 rounded-lg shadow-sm hover:bg-emerald-50 dark:hover:bg-emerald-900/20 hover:border-emerald-400 dark:hover:border-emerald-600 transition-colors"
              >
                {isPickerLoading ? (
                  <ArrowPathIcon className="w-5 h-5 animate-spin text-emerald-600" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 87.3 78" xmlns="http://www.w3.org/2000/svg">
                    <path d="m6.6 66.85 3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3l13.75-23.8h-27.5c0 1.55.4 3.1 1.2 4.5z" fill="#0066da"/>
                    <path d="m43.65 25-13.75-23.8c-1.35.8-2.5 1.9-3.3 3.3l-20.4 35.3c-.8 1.4-1.2 2.95-1.2 4.5h27.5z" fill="#00ac47"/>
                    <path d="m73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75 7.65-13.25c.8-1.4 1.2-2.95 1.2-4.5h-27.5l5.4 13.8z" fill="#ea4335"/>
                    <path d="m43.65 25 13.75-23.8c-1.35-.8-2.9-1.2-4.5-1.2h-18.5c-1.6 0-3.15.45-4.5 1.2z" fill="#00832d"/>
                    <path d="m59.8 53h-32.3l-13.75 23.8c1.35.8 2.9 1.2 4.5 1.2h50.8c1.6 0 3.15-.45 4.5-1.2z" fill="#2684fc"/>
                    <path d="m73.4 26.5-10.1-17.5c-.8-1.4-1.95-2.5-3.3-3.3l-13.75 23.8 16.15 23.8h27.45c0-1.55-.4-3.1-1.2-4.5z" fill="#ffba00"/>
                  </svg>
                )}
                <span className="font-medium text-surface-900 dark:text-white">
                  {isPickerLoading ? 'Opening Picker...' : 'Open Google Drive Picker'}
                </span>
              </button>

              {/* Info note */}
              <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 text-sm max-w-md">
                <ExclamationCircleIcon className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-blue-700 dark:text-blue-300">
                  Only the spreadsheets you explicitly select will be accessible to NeoShift BI. We never access other files in your Drive.
                </p>
              </div>
            </div>
          )}
          
          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={onBack} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 3: Select Sheet */}
      {step === 3 && (
        <div className="card p-6">
          <div className="flex items-center gap-3 mb-4">
            <GoogleSheetsIcon className="w-6 h-6 text-emerald-500" />
            <div>
              <h3 className="text-lg font-semibold text-surface-900 dark:text-white">
                Select Sheet
              </h3>
              <p className="text-sm text-surface-500">
                {selectedSpreadsheet?.name}
              </p>
            </div>
          </div>
          
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
            </div>
          ) : sheets.length === 0 ? (
            <div className="text-center py-8 text-surface-500">
              No sheets found in this spreadsheet
            </div>
          ) : (
            <div className="grid gap-2">
              {sheets.map((sheet) => (
                <button
                  key={sheet.sheetId}
                  onClick={() => handleSelectSheet(sheet)}
                  className="flex items-center gap-3 p-3 rounded-lg border border-surface-200 dark:border-surface-700 hover:border-emerald-300 text-left transition-colors"
                >
                  <TableCellsIcon className="w-5 h-5 text-surface-400" />
                  <div className="flex-1">
                    <p className="font-medium text-surface-900 dark:text-white">
                      {sheet.title}
                    </p>
                    {sheet.rowCount && (
                      <p className="text-sm text-surface-500">
                        ~{sheet.rowCount.toLocaleString()} rows, {sheet.columnCount} columns
                      </p>
                    )}
                  </div>
                  <ChevronRightIcon className="w-5 h-5 text-surface-400" />
                </button>
              ))}
            </div>
          )}
          
          <div className="flex justify-between pt-4 mt-4 border-t border-surface-200 dark:border-surface-700">
            <button onClick={() => {
              setSelectedSpreadsheet(null);
              setSheets([]);
              setStep(2);
            }} className="btn-secondary">
              Back
            </button>
          </div>
        </div>
      )}
      
      {/* Step 4: Configure & Preview */}
      {step === 4 && (
        <div className="space-y-6">
          {/* Name input */}
          <div className="card p-4">
            <label className="block text-sm font-medium text-surface-700 dark:text-surface-300 mb-2">
              Data Source Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input"
              placeholder={`${selectedSpreadsheet?.name} - ${selectedSheet?.title}`}
            />
          </div>
          
          {/* Column Selection */}
          {columns.length > 0 && (
            <div className="card p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-surface-900 dark:text-white">
                  Select Columns ({selectedColumns.size} of {columns.length})
                </h4>
                <button
                  onClick={handleSelectAllColumns}
                  className="text-sm text-emerald-600 dark:text-emerald-400 hover:underline"
                >
                  {selectedColumns.size === columns.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              
              {/* Column Search */}
              {columns.length > 8 && (
                <div className="relative mb-3">
                  <MagnifyingGlassIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
                  <input
                    type="text"
                    value={columnSearchTerm}
                    onChange={(e) => setColumnSearchTerm(e.target.value)}
                    placeholder="Search columns..."
                    className="input pl-9 py-2 text-sm w-full"
                  />
                  {columnSearchTerm && (
                    <button
                      onClick={() => setColumnSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                    >
                      ×
                    </button>
                  )}
                </div>
              )}
              
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                {columns
                  .filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase()))
                  .map((col) => (
                  <label
                    key={col.name}
                    className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                      selectedColumns.has(col.name)
                        ? 'bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800'
                        : 'bg-surface-50 dark:bg-surface-800 border border-transparent hover:bg-surface-100 dark:hover:bg-surface-700'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedColumns.has(col.name)}
                      onChange={() => handleColumnToggle(col.name)}
                      className="rounded text-emerald-600"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-surface-900 dark:text-white truncate" title={col.name}>
                        {col.name}
                      </p>
                      <p className="text-xs text-surface-500">{col.type}</p>
                    </div>
                  </label>
                ))}
                {columns.filter(col => col.name.toLowerCase().includes(columnSearchTerm.toLowerCase())).length === 0 && (
                  <div className="col-span-full text-center py-4 text-surface-500 text-sm">
                    No columns match "{columnSearchTerm}"
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Data Preview */}
          <div className="card p-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-surface-900 dark:text-white">
                Data Preview
              </h4>
              <span className="text-sm text-surface-500">
                Showing {Math.min(previewData.length, 10)} rows
              </span>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <ArrowPathIcon className="w-8 h-8 text-emerald-500 animate-spin" />
              </div>
            ) : selectedColumns.size === 0 ? (
              <div className="text-center py-8 text-surface-500">
                Please select at least one column to preview
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-surface-200 dark:border-surface-700">
                <table className="w-full text-sm">
                  <thead className="bg-surface-50 dark:bg-surface-800">
                    <tr>
                      {columns.filter(c => selectedColumns.has(c.name)).map((col, i) => (
                        <th key={i} className="px-4 py-2 text-left font-medium text-surface-600 dark:text-surface-400 whitespace-nowrap">
                          {col.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 10).map((row, i) => (
                      <tr key={i} className="border-t border-surface-100 dark:border-surface-800">
                        {columns.filter(c => selectedColumns.has(c.name)).map((col, j) => (
                          <td key={j} className="px-4 py-2 text-surface-700 dark:text-surface-300 whitespace-nowrap max-w-xs truncate" title={String(row[col.name] ?? '')}>
                            {String(row[col.name] ?? '')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Actions */}
          <div className="flex justify-between">
            <button onClick={() => setStep(3)} className="btn-secondary">
              Back
            </button>
            <button
              onClick={handleCreate}
              disabled={isCreating || !name.trim() || selectedColumns.size === 0}
              className="btn-primary"
            >
              {isCreating ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <CheckCircleIcon className="w-5 h-5" />
                  Create Data Source
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
